/* ═══════════════════════════════════════════════════════════════
   Spec Masters Studio — the workspace.

   The SAME dockview host the Designer and the Forma Studio use
   (components/FormaDock), with the §2.8 panel idiom on top: a panel's
   toolbar button drops it down anchored, or drags it into a dock zone /
   out to float. Nothing here re-implements docking, dragging, layout
   persistence or the firm-default ladder — FormaDock owns all of it.

   Two palettes, because a master is CONTENT and the canvas is the hero
   (DESIGN §3.4):
     · Outline   — the heading map, straight from the Designer's
                   NavigationPalette, reading the live document
     · Blocks    — insert AND level (Modules R2 Phase 6 amends Ruling 9:
                   level returns here — snap 1-9 + a relative ±1 step,
                   both multi-select aware — alongside the toolbar's
                   Paragraph group and Tab/Shift+Tab, which stay untouched)
     · Modules   — the firm's reusable block-groups, and the chunks an
                   insert could not place on its own (Phase 5)
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import {
  Blocks,
  EyeOff,
  GitPullRequestArrow,
  Image as ImageGlyph,
  Link2,
  ListChecks,
  ListTree,
  Group as GroupGlyph,
  Ungroup as UngroupGlyph,
  PackagePlus,
  Pilcrow,
  Plus,
  SquarePlus,
  Table as TableToolboxGlyph,
} from 'lucide-react';
import type { Editor } from '@tiptap/react';
import type { ContentBlock } from '../../shared/types';
import { FormaDock, type FormaDockHandle, type PaletteDef } from '../components/FormaDock';
import { ErrorBoundary } from '../components/ui/ErrorBoundary';
import { Icon } from '../components/ui/Icon';
import { NavigationPalette, type NavItem } from '../designer/Navigation';
import { PanelDropdown } from '../editor-chrome/PanelDropdown';
import { TablesPalette } from '../designer/TablesPalette';
import { ToolPanel } from '../editor-chrome/ToolPanel';
import type { FloatingTool } from '../editor-chrome/dock';
import {
  canShiftLevels,
  insertImageBlock,
  insertRelatedSectionsBlock,
  pickImageFile,
  selectedBlocks,
  setLevel,
  shiftSelectedLevels,
  toggleExcluded,
} from '../designer/commands';
import {
  addBlockBelow,
  focusBlockAt,
  GROUP_MODULE_REFUSAL,
  groupSelectedBlocks,
  planGroup,
  selectionHasGroup,
  toggleContinuation,
  ungroupSelectedBlocks,
} from './commands';
import {
  assignModulePartMany,
  insertModuleArticleAtPartEnd,
  NEW_PARAGRAPH_DRAG_MIME,
  partAtCaret,
} from './moduleBuild';
import { selectionPart, moveSelectionToPart } from './moduleSelectionPart';
import { MODULE_PARTS, type ModulePart } from '../../shared/spec-modules';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { unseededRelatedParams } from '../designer/RelatedSections';

/** MasterFormat's nine levels — the same range every level control clamps to. */
const LEVEL_NUMS = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const;

/** Where the firm's default Spec Studio layout lives (DESIGNER §2.9). */
export const SPEC_STUDIO_FIRM_LAYOUT_KEY = 'sf.specStudioDock.firmDefault.v1';

export const SPEC_STUDIO_PALETTES: PaletteDef[] = [
  { id: 'canvas', title: 'Document', closable: false, noTab: true },
  {
    id: 'outline',
    title: 'Outline',
    width: 268,
    defaultPosition: { direction: 'left', reference: 'canvas' },
  },
  {
    id: 'blocks',
    title: 'Blocks',
    width: 288,
    defaultPosition: { direction: 'right', reference: 'canvas' },
  },
  // T1.2 — every table command in one dockable toolbox (the floating table
  // context toolbar is retired). Docked below Blocks by default: inserting
  // is a Blocks action, everything past that is a table action.
  {
    id: 'tables',
    title: 'Tables',
    width: 288,
    defaultPosition: { direction: 'below', reference: 'blocks' },
  },
  // Phase 3 — what the office master's last publish means for the client
  // version on screen. One more dock panel, never a bespoke drawer. Opened on
  // demand (its toolbar button carries the count, and a block's "Update" tag
  // drops it down): a third panel in the DEFAULT layout would squeeze the
  // sheet, and the sheet is the hero (DESIGN §3.4).
  {
    id: 'updates',
    title: 'Updates',
    width: 340,
    defaultPosition: { direction: 'right', reference: 'canvas' },
    defaultClosed: true,
  },
  // Phase 5 — the module library, and the tray of chunks still looking for a
  // spot. Closed by default for the same reason Updates is: it is a panel you
  // reach for, and the sheet is the hero (DESIGN §3.4). Offered only to people
  // who hold `modules.view` — see `specStudioPalettes` below.
  {
    id: 'modules',
    title: 'Modules',
    width: 320,
    // See designer/Workspace.tsx's identical note — the module table needs
    // a usable floor above the app default (220×140), still well inside
    // the empty-side-drop trap (DESIGN §3.5).
    minWidth: 260,
    minHeight: 220,
    defaultPosition: { direction: 'right', reference: 'canvas' },
    defaultClosed: true,
  },
];

/** The palettes THIS user is offered. A panel nobody may open is not shown
 *  disabled — the library is simply not part of their Studio (DESIGN §4:
 *  disable what you may not do YET, hide what is not yours at all). */
export function specStudioPalettes(canViewModules: boolean): PaletteDef[] {
  return canViewModules
    ? SPEC_STUDIO_PALETTES
    : SPEC_STUDIO_PALETTES.filter((p) => p.id !== 'modules');
}

export const SPEC_STUDIO_PANEL_ICONS: Record<string, React.ReactNode> = {
  outline: <Icon icon={ListTree} em={1.1538} aria-hidden="true" />,
  blocks: <Icon icon={SquarePlus} em={1.1538} aria-hidden="true" />,
  tables: <Icon icon={TableToolboxGlyph} em={1.1538} aria-hidden="true" />,
  updates: <Icon icon={GitPullRequestArrow} em={1.1538} aria-hidden="true" />,
  modules: <Icon icon={Blocks} em={1.1538} aria-hidden="true" />,
};

const titleOf = (id: string) => SPEC_STUDIO_PALETTES.find((p) => p.id === id)?.title || id;

/** Calls `body(id)` DURING React's own render of this component — not
 *  during the caller's. A throw inside a plain function call (the old
 *  shape: `<ErrorBoundary>{renderPaletteBody(id)}</ErrorBoundary>`) happens
 *  while the PARENT is still computing its children, which is outside any
 *  descendant boundary's reach — React only catches a throw that occurs
 *  while rendering a DESCENDANT component. Routing the call through this
 *  tiny component makes it one, so the `ErrorBoundary` wrapping it actually
 *  catches it instead of the throw bubbling past it to the page boundary. */
function PaletteBody({ id, body }: { id: string; body: (id: string) => React.ReactNode }) {
  return <>{body(id)}</>;
}

/* ─── The Blocks palette: insert + level, all visible, all clickable.
       Level returned here (R2 Phase 6 amends Ruling 9): a snap grid (1-9)
       and a relative ±1 step, both acting on whatever the gutter/caret
       selection resolves to — the SAME `selectedBlocks` every other
       whole-block command (duplicate, delete, the toolbar's own level
       cluster) already stands on, so multi-select just works here too.
       Tab/Shift+Tab and the toolbar Paragraph group are untouched. ─── */

export function BlocksPalette({
  editor,
  canEdit,
  moduleMode = false,
  onSaveAsModule,
  canSaveAsModule = false,
}: {
  editor: Editor | null;
  canEdit: boolean;
  /** Module editor (Cohesion R1 Phase 2 item 1): parts are the fixed
   *  pseudo-structure, so level 1 is never a paragraph a writer sets — the
   *  snap grid and the shift-shallower step both floor at 2. */
  moduleMode?: boolean;
  /** Spec Studio only (Cohesion R1 Phase 4): opens the Save-as-module
   *  capture dialog. Undefined in module mode — a module never nests
   *  another module, so the group simply doesn't render there. */
  onSaveAsModule?: () => void;
  /** Holds modules.create or modules.edit — either one earns the button. */
  canSaveAsModule?: boolean;
}) {
  // Follows the caret AND the gutter multi-selection — both live in the
  // editor's ProseMirror state, so one transaction subscription covers both
  // (a selection-only dispatch still fires TipTap's 'transaction' event).
  const [, tick] = React.useReducer((n: number) => n + 1, 0);
  React.useEffect(() => {
    if (!editor) return;
    editor.on('transaction', tick);
    return () => {
      editor.off('transaction', tick);
    };
  }, [editor]);

  const dispatch = editor ? editor.view.dispatch.bind(editor.view) : undefined;
  const disabled = !editor || !canEdit;

  const selected = editor ? selectedBlocks(editor.state) : [];
  const levels = selected.map(({ node }) => (node.attrs.level as number) || 1);
  const uniformLevel = levels.length && levels.every((l) => l === levels[0]) ? levels[0] : null;
  // "Continuation" and "Skip at export" (Cohesion R1 Phase 4 — moved off the
  // Studio top bar; Ctrl+Shift+C and the SpecBlockMenu entries are unchanged,
  // this is only their toolbar home). Continuation reads/writes the same
  // paragraph attribute the numbering brain already treats as universal
  // (`spec-modules.ts`), so it applies in module mode too. Skip at export
  // never appears in module mode (Phase 1: modules clear `excluded` on save
  // and the context-menu entry is already hidden there).
  const continuationOn = selected.length > 0 && selected.every((b) => !!b.node.attrs.continuation);
  const excludedOn = selected.length > 0 && selected.every((b) => !!b.node.attrs.excluded);
  const saveAsModuleCount = selected.length;
  const levelLabel =
    levels.length > 1
      ? `${levels.length} selected`
      : uniformLevel === null
        ? 'Level —'
        : `Level ${uniformLevel}`;

  // Module mode's floor: parts are fixed pseudo-structure (Cohesion R1
  // Phase 1), so level 1 is never a paragraph a writer sets from here.
  const minLevel = moduleMode ? 2 : 1;
  const levelNums = moduleMode ? LEVEL_NUMS.filter((n) => n >= 2) : LEVEL_NUMS;
  const canStepShallower = canShiftLevels(levels, -1) && levels.every((l) => l - 1 >= minLevel);

  // Module mode's Part group (Cohesion R1 follow-up, Josh 2026-08-30 ruling:
  // moved off the right-click menu — a context menu is not where structural
  // placement belongs). Works on the WHOLE selection when every selected
  // block is a level-2 article (Module Zones Phase 1) — a mixed selection
  // (an article plus its body text, say) has nothing uniform to set, so the
  // group stands down rather than guess. `assignModulePartMany` both stamps
  // `modulePart` on every targeted article and relocates the run to the end
  // of that part's group, in one undoable transaction (moduleBuild.ts).
  const selectedArticles =
    selected.length > 0 && selected.every((b) => (b.node.attrs.level as number) === 2)
      ? selected
      : [];
  const partsInSelection: Array<ModulePart | undefined> = selectedArticles.map((b) => {
    const raw = (b.node.attrs.meta as Record<string, unknown> | undefined)?.modulePart;
    return raw === 1 || raw === 2 || raw === 3 ? (raw as ModulePart) : undefined;
  });
  // A single, shared part when every selected article agrees; `null` marks
  // a MIXED selection (some articles differ or are unset) — indeterminate,
  // not "off", so the toolbox doesn't lie about state it can't summarize.
  // Anything that is not purely articles (a paragraph, or a mix) follows the
  // article above it: show that inherited part, and move the selection with
  // the same header-drop landing the drag uses (moduleSelectionPart.ts).
  const selectedIds = selected.map((b) => b.node.attrs.blockId as string);
  const paragraphMode = moduleMode && selected.length > 0 && !selectedArticles.length;
  const currentPart: ModulePart | undefined | null = paragraphMode
    ? editor
      ? selectionPart(editor.state, selectedIds)
      : undefined
    : !selectedArticles.length
      ? undefined
      : partsInSelection.every((p) => p === partsInSelection[0])
        ? partsInSelection[0]
        : null;
  const hasPartTarget = selectedArticles.length > 0 || paragraphMode;
  const partDisabled = disabled || !hasPartTarget;
  const setPart = (part: ModulePart) => {
    if (!editor || !hasPartTarget) return;
    if (paragraphMode) {
      moveSelectionToPart(editor.state, dispatch, selectedIds, part);
    } else {
      assignModulePartMany(
        editor.state,
        dispatch,
        selectedArticles.map((b) => b.node.attrs.blockId as string),
        part
      );
    }
    editor.commands.focus();
  };

  // "New paragraph" (Module Zones Phase 2, Josh 2026-08-30 ruling): an empty
  // article placed by PART, two ways — click opens this popover, drag lands
  // it wherever the canvas's three zones say (BlockGutter reads the same
  // MIME marker off the drag). The popover's default highlight is the part
  // the CARET is already in, not the gutter selection — a writer opens this
  // mid-typing far more often than with a block picked.
  const [newParaOpen, setNewParaOpen] = React.useState(false);
  const caretPart = editor && moduleMode ? (partAtCaret(editor.state) ?? 1) : 1;
  const addNewParagraph = (part: ModulePart) => {
    if (!editor) return;
    setNewParaOpen(false);
    insertModuleArticleAtPartEnd(editor.state, dispatch, part);
    editor.commands.focus();
  };

  // Grouping (T3). A group keeps a run of blocks together: one grip, one
  // drag, one delete. Both buttons stay VISIBLE and explain themselves when
  // they cannot run (DESIGN §4) — never silently absent.
  const groupPlan = editor ? planGroup(editor.state) : null;
  const hasGroup = !!editor && selectionHasGroup(editor.state);
  const doGroup = () => {
    if (!editor) return;
    groupSelectedBlocks(editor.state, dispatch);
    editor.commands.focus();
  };
  const doUngroup = () => {
    if (!editor) return;
    ungroupSelectedBlocks(editor.state, dispatch);
    editor.commands.focus();
  };

  const snap = (n: number) => {
    if (!editor) return;
    setLevel(editor.state, dispatch, n);
    editor.commands.focus();
  };
  const step = (delta: -1 | 1) => {
    if (!editor) return;
    if (delta === -1 && !canStepShallower) return;
    shiftSelectedLevels(editor.state, dispatch, delta);
    editor.commands.focus();
  };

  return (
    // sf-palette-flow: Insert/Level/Flags tile side-by-side when this panel
    // is docked top/bottom instead of stacking (DESIGN.md §3.5).
    <div className="dsg-pal sps-pal sf-palette-flow">
      <div className="sps-pal-group" role="group" aria-label="Insert">
        <h3 className="sps-pal-head">Insert</h3>
        <button
          type="button"
          className="dsg-btn sps-pal-btn"
          disabled={disabled}
          onClick={() => editor && addBlockBelow(editor.state, dispatch)}
        >
          <Icon icon={Plus} em={1.0769} aria-hidden="true" className="sps-pal-btn-icon" />
          Block below
        </button>
        <button
          type="button"
          className="dsg-btn sps-pal-btn"
          disabled={disabled}
          onClick={() => {
            void pickImageFile().then((img) => {
              if (img && editor) insertImageBlock(editor.state, dispatch, img.dataUrl, img.name);
            });
          }}
          title="Insert an image below the current block"
        >
          <Icon icon={ImageGlyph} em={1.0769} aria-hidden="true" className="sps-pal-btn-icon" />
          Image
        </button>
        {/* The one smart block (Josh 2026-10-02). A placeholder here — a master
            has no project — that lists the other sections once the spec is
            added to one. Never in a module: modules are static content. */}
        {!moduleMode && (
          <button
            type="button"
            className="dsg-btn sps-pal-btn"
            disabled={disabled}
            aria-label="Insert related sections"
            onClick={() => {
              if (!editor) return;
              insertRelatedSectionsBlock(editor.state, dispatch, unseededRelatedParams());
              editor.commands.focus();
            }}
            title="Insert a block that lists the other sections once this spec is in a project"
          >
            <Icon icon={ListChecks} em={1.0769} aria-hidden="true" className="sps-pal-btn-icon" />
            Related sections
          </button>
        )}
        {moduleMode && (
          <Popover open={newParaOpen} onOpenChange={setNewParaOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="dsg-btn sps-pal-btn"
                disabled={disabled}
                draggable={!disabled}
                onDragStart={(e) => {
                  setNewParaOpen(false);
                  e.dataTransfer.setData(NEW_PARAGRAPH_DRAG_MIME, '1');
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                title="Click for which part, or drag it straight onto one of the three zones"
              >
                <Icon icon={Pilcrow} em={1.0769} aria-hidden="true" className="sps-pal-btn-icon" />
                New paragraph
              </button>
            </PopoverTrigger>
            <PopoverContent className="sps-newpara-pop" align="start" sideOffset={6}>
              <p className="sps-pal-hint">Which part does this new article belong to?</p>
              <div className="sps-part-grid" role="radiogroup" aria-label="New paragraph — part">
                {MODULE_PARTS.map((part) => (
                  <button
                    key={part}
                    type="button"
                    role="radio"
                    aria-checked={caretPart === part}
                    className={`sps-level-num${caretPart === part ? ' is-on' : ''}`}
                    onClick={() => addNewParagraph(part)}
                    title={`Add an empty paragraph at the end of Part ${part}`}
                  >
                    {part}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>

      <div className="sps-pal-group" role="group" aria-label="Level">
        <h3 className="sps-pal-head">Level</h3>
        <p className="sps-pal-hint">
          Snap the selected paragraph(s) to a level, or shift them together and keep their relative
          levels. Tab and Shift+Tab do the same thing one paragraph at a time.
        </p>
        <div className="sps-level-grid" role="radiogroup" aria-label="Set level">
          {levelNums.map((n) => (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={uniformLevel === n}
              className={`sps-level-num${uniformLevel === n ? ' is-on' : ''}`}
              disabled={disabled}
              onClick={() => snap(n)}
              title={`Level ${n}`}
            >
              {n}
            </button>
          ))}
        </div>
        <div className="dsg-group sps-level-step" role="group" aria-label="Shift level">
          <button
            type="button"
            className="dsg-icon-btn"
            disabled={disabled || !canStepShallower}
            onClick={() => step(-1)}
            aria-label="Shift the selection one level shallower"
            title="Shift every selected paragraph one level shallower, together"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M11 5h10M11 12h10M11 19h10M7 9l-4 3 4 3" />
            </svg>
          </button>
          <span className="dsg-level-value" aria-live="polite">
            {levelLabel}
          </span>
          <button
            type="button"
            className="dsg-icon-btn"
            disabled={disabled || !canShiftLevels(levels, 1)}
            onClick={() => step(1)}
            aria-label="Shift the selection one level deeper"
            title="Shift every selected paragraph one level deeper, together"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M11 5h10M11 12h10M11 19h10M3 9l4 3-4 3" />
            </svg>
          </button>
        </div>
      </div>

      {moduleMode && (
        <div className="sps-pal-group" role="group" aria-label="Part">
          <h3 className="sps-pal-head">Part</h3>
          <p className="sps-pal-hint">
            {paragraphMode
              ? 'Paragraphs follow their article. Pick a part to move the selection there.'
              : selectedArticles.length > 1
                ? currentPart === null
                  ? `${selectedArticles.length} articles selected — parts differ. Pick one to set them all.`
                  : `Which part these ${selectedArticles.length} articles belong to.`
                : selectedArticles.length === 1
                  ? 'Which part this article belongs to.'
                  : 'Select one or more articles to set their part.'}
          </p>
          <div className="sps-part-grid" role="radiogroup" aria-label="Set part">
            {MODULE_PARTS.map((part) => (
              <button
                key={part}
                type="button"
                role="radio"
                aria-checked={currentPart === part}
                className={`sps-level-num${currentPart === part ? ' is-on' : ''}${
                  currentPart === null ? ' is-mixed' : ''
                }`}
                disabled={partDisabled}
                onClick={() => setPart(part)}
                title={
                  !hasPartTarget
                    ? 'Select an article or paragraph first'
                    : currentPart === null
                      ? `Parts differ across the selection — click to set all to Part ${part}`
                      : currentPart === part
                        ? `In Part ${part}`
                        : paragraphMode
                          ? `Move the selection to Part ${part}`
                          : `Put ${selectedArticles.length > 1 ? 'these articles' : 'this article'} in Part ${part}`
                }
              >
                {part}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="sps-pal-group" role="group" aria-label="Paragraph flags">
        <h3 className="sps-pal-head">Flags</h3>
        <button
          type="button"
          className={`dsg-btn sps-pal-btn${continuationOn ? ' dsg-tool-on' : ''}`}
          aria-pressed={continuationOn}
          disabled={disabled}
          onClick={() => editor && toggleContinuation(editor.state, dispatch!)}
          title="No number — flows under the previous paragraph. (Ctrl+Shift+C)"
        >
          <Icon icon={Link2} em={1.0769} aria-hidden="true" className="sps-pal-btn-icon" />
          Continuation
        </button>
        {!moduleMode && (
          <button
            type="button"
            className={`dsg-btn sps-pal-btn${excludedOn ? ' dsg-tool-on' : ''}`}
            aria-pressed={excludedOn}
            disabled={disabled}
            onClick={() => editor && toggleExcluded(editor.state, dispatch!)}
            title={
              excludedOn
                ? 'Skipped — left out of the exported document. Click to include it again.'
                : 'Skip at export — the block stays here but is left out of the exported document.'
            }
          >
            <Icon icon={EyeOff} em={1.0769} aria-hidden="true" className="sps-pal-btn-icon" />
            {excludedOn ? 'Skipped' : 'Skip at export'}
          </button>
        )}
      </div>

      <div className="sps-pal-group" role="group" aria-label="Group">
        <h3 className="sps-pal-head">Group</h3>
        <p className="sps-pal-hint">
          Blocks in a group stay together — one grip, and they move, copy and delete as one.
        </p>
        <button
          type="button"
          className="dsg-btn sps-pal-btn"
          disabled={disabled || !groupPlan || !!groupPlan.refusal}
          onClick={doGroup}
          title={
            groupPlan?.refusal === 'module'
              ? `${GROUP_MODULE_REFUSAL} — select the whole insertion, or leave it out`
              : groupPlan?.refusal === 'few' || !groupPlan
                ? 'Pick two or more blocks with the gutter grips first'
                : `Group these ${groupPlan.ids.length} blocks. (Ctrl+G)`
          }
        >
          <Icon icon={GroupGlyph} em={1.0769} aria-hidden="true" className="sps-pal-btn-icon" />
          Group
        </button>
        <button
          type="button"
          className="dsg-btn sps-pal-btn"
          disabled={disabled || !hasGroup}
          onClick={doUngroup}
          title={
            hasGroup
              ? 'Let these blocks travel on their own again. (Ctrl+Shift+G)'
              : 'Select a grouped block first'
          }
        >
          <Icon icon={UngroupGlyph} em={1.0769} aria-hidden="true" className="sps-pal-btn-icon" />
          Ungroup
        </button>
      </div>

      {!moduleMode && onSaveAsModule && (
        <div className="sps-pal-group" role="group" aria-label="Module">
          <h3 className="sps-pal-head">Module</h3>
          <button
            type="button"
            className="dsg-btn sps-pal-btn"
            disabled={!editor || !canSaveAsModule || saveAsModuleCount === 0}
            onClick={onSaveAsModule}
            title={
              !canSaveAsModule
                ? 'Saving modules needs the Save Modules privilege'
                : saveAsModuleCount === 0
                  ? 'Select blocks with the gutter grips first, then save them as a reusable module'
                  : `Save ${saveAsModuleCount} selected block${saveAsModuleCount === 1 ? '' : 's'} to the module library`
            }
          >
            <Icon icon={PackagePlus} em={1.0769} aria-hidden="true" className="sps-pal-btn-icon" />
            Save as module{saveAsModuleCount > 0 ? ` (${saveAsModuleCount})` : ''}
          </button>
        </div>
      )}
    </div>
  );
}

export interface SpecStudioWorkspaceProps {
  isDark: boolean;
  userId: string;
  locked: boolean;
  editor: Editor | null;
  canEdit: boolean;
  floatingTools: FloatingTool[];
  onCloseFloating: (id: string) => void;
  onDockFloating: (id: string) => void;
  dropdown: { id: string; anchor: { left: number; bottom: number } } | null;
  onCloseDropdown: () => void;
  onFloatDropdown: (id: string, at: { left: number; top: number }) => void;
  onDockPanelsChange: (ids: string[]) => void;
  onDockReady: (handle: FormaDockHandle) => void;
  canvas: React.ReactNode;
  /** The Updates panel's body — built by the Studio, which owns the feed
   *  state, and portalled wherever the panel currently lives (dock, float or
   *  toolbar dropdown) exactly like the other palettes. */
  updates: React.ReactNode;
  /** The Modules panel's body (Phase 5), on the same terms. Null when this
   *  user has no `modules.view` — the palette is then not offered at all. */
  modules: React.ReactNode;
  /** Save-as-module (Cohesion R1 Phase 4 — moved off the top bar into the
   *  Blocks toolbox). Opens the capture dialog Studio.tsx owns. */
  onSaveAsModule: () => void;
  canSaveAsModule: boolean;
  /** Track changes is showing (no live editor): the outline reads these
   *  blocks instead, and a click scrolls to the row with that id. */
  outlineBlocks?: ContentBlock[] | null;
  onOutlineJump?: (blockId: string) => void;
}

export function SpecStudioWorkspace({
  isDark,
  userId,
  locked,
  editor,
  canEdit,
  floatingTools,
  onCloseFloating,
  onDockFloating,
  dropdown,
  onCloseDropdown,
  onFloatDropdown,
  onDockPanelsChange,
  onDockReady,
  canvas,
  updates,
  modules,
  onSaveAsModule,
  canSaveAsModule,
  outlineBlocks,
  onOutlineJump,
}: SpecStudioWorkspaceProps) {
  const [dockEls, setDockEls] = React.useState<Record<string, HTMLElement>>({});
  const palettes = React.useMemo(() => specStudioPalettes(!!modules), [modules]);

  React.useEffect(() => {
    onDockPanelsChange(Object.keys(dockEls));
  }, [dockEls, onDockPanelsChange]);

  const jump = React.useCallback(
    (item: NavItem) => {
      if (!editor) return;
      focusBlockAt(editor.state, editor.view.dispatch.bind(editor.view), item.pos);
      editor.commands.focus();
    },
    [editor]
  );

  const renderPaletteBody = React.useCallback(
    (id: string): React.ReactNode => {
      if (id === 'outline') {
        return outlineBlocks ? (
          <NavigationPalette
            editor={null}
            forma={null}
            blocks={outlineBlocks}
            onJump={(item) => onOutlineJump?.(item.id)}
          />
        ) : (
          <NavigationPalette editor={editor} forma={null} onJump={jump} />
        );
      }
      if (id === 'blocks')
        return (
          <BlocksPalette
            editor={editor}
            canEdit={canEdit}
            onSaveAsModule={onSaveAsModule}
            canSaveAsModule={canSaveAsModule}
          />
        );
      if (id === 'tables') return <TablesPalette editor={editor} canEdit={canEdit} />;
      if (id === 'updates') return updates;
      if (id === 'modules') return modules;
      return null;
    },
    [
      editor,
      jump,
      canEdit,
      updates,
      modules,
      onSaveAsModule,
      canSaveAsModule,
      outlineBlocks,
      onOutlineJump,
    ]
  );

  // Every toolbox panel body — docked, floating, or dropped down from the
  // toolbar — renders through this ONE function, so a single boundary here
  // covers all three without the canvas (a separate portal, never nested
  // inside this tree) ever going down with it.
  const renderPalette = React.useCallback(
    (id: string): React.ReactNode => (
      <ErrorBoundary title={`${titleOf(id)} hit an error`}>
        <PaletteBody id={id} body={renderPaletteBody} />
      </ErrorBoundary>
    ),
    [renderPaletteBody]
  );

  return (
    <div className="dsg-workspace sps-workspace">
      <FormaDock
        isDark={isDark}
        locked={locked}
        palettes={palettes}
        layoutKey={`sf.specStudioDock.v1.${userId || 'anon'}`}
        firmLayoutKey={SPEC_STUDIO_FIRM_LAYOUT_KEY}
        panelIcons={SPEC_STUDIO_PANEL_ICONS}
        onContainersChange={setDockEls}
        onReady={onDockReady}
      />

      {dockEls.canvas && createPortal(canvas, dockEls.canvas)}

      {palettes
        .filter((p) => p.id !== 'canvas')
        .map((p) =>
          dockEls[p.id] ? (
            <React.Fragment key={p.id}>
              {createPortal(renderPalette(p.id), dockEls[p.id])}
            </React.Fragment>
          ) : null
        )}

      {/* Floating panels — the SAME bodies, no dock required. */}
      {floatingTools
        .filter((f) => !dockEls[f.id])
        .map((f, i) => (
          <ToolPanel
            key={f.id}
            title={titleOf(f.id)}
            index={i}
            at={f.at}
            onDock={() => onDockFloating(f.id)}
            onClose={() => onCloseFloating(f.id)}
          >
            {renderPalette(f.id)}
          </ToolPanel>
        ))}

      {/* The panel dropped down from its toolbar button (§2.8). */}
      {dropdown && !dockEls[dropdown.id] && (
        <PanelDropdown
          title={titleOf(dropdown.id)}
          anchor={dropdown.anchor}
          onFloat={() =>
            onFloatDropdown(dropdown.id, {
              left: dropdown.anchor.left + 180,
              top: dropdown.anchor.bottom + 14,
            })
          }
          onClose={onCloseDropdown}
        >
          {renderPalette(dropdown.id)}
        </PanelDropdown>
      )}
    </div>
  );
}
